import { Add, AddToPhotos, Delete, Save } from '@mui/icons-material';
import { Button, IconButton, Typography, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import * as Sentry from '@sentry/react';
import SentryWrap from 'components/Sentry';
import { Col, Progress, Row } from 'antd';
import MultiFileUploader from 'components/UI/admin/MultifileUploadGallery';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { useCallback, useRef, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';
import { UPLOAD_IMAGE_TO_FIREBASE } from 'utils/constants/enum';
import { imageUploader } from 'utils/methods/imageUploader';
import AdminActivity from 'components/AdminBooks/adminActivity';
import { useDispatch } from 'react-redux';
import { DeleteModal } from 'components/Dialog';
import {
  handleAdminDeleteEnageActivity,
  handleAdminDeleteEnageActivitySequence,
} from 'store/activities/thunks';
import { debounce } from 'lodash';
import { v4 } from 'uuid';

const fileTypes = ['JPG', 'PNG', 'GIF', 'JPEG'];

const ImageContainer = styled.div`
  height: auto;
  flex: 1;
  max-height: 70vh;
  overflow-y: auto;
`;

const Images = styled.img`
  background-color: ${COLOR.white};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  max-width: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: ${(p) => (p.empty ? 'rgba(0,0,0,0.05)' : 'transparent')};
`;

const Page = styled.div`
  align-items: center;
  color: ${COLOR.black100};
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  margin-bottom: 10px;
`;

const Input = styled.input`
  background-color: transparent;
  border: 1px dashed gray;
  border-radius: 3px;
  box-shadow: none;
  font-size: inherit;
  font-weight: 400;
  outline: none;
  padding: 4px 8px;
  transition: all 300ms ease;
  width: 250px;
  margin-left: 8px;
`;

const FileUploadArea = styled.div`
  height: 120px;
  border: 2px dashed ${(props) => (props.isDragOver ? '#1976d2' : '#ccc')};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background-color: ${(props) => (props.isDragOver ? '#f5f5f5' : 'transparent')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1976d2;
    background-color: #f5f5f5;
  }
`;

const Text = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-top: 8px;
`;

const ImageWrapper = styled.div`
  border: 1px solid #e0e0e0;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
  position: relative;
`;

const ImageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const UploadProgress = styled.div`
  margin: 10px 0;
`;

const MultiUploadArea = styled.div`
  border: 2px dashed #1976d2;
  padding: 30px;
  border-radius: 12px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  text-align: center;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 15px;
`;

const PreviewItem = styled.div`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
`;

const DeleteButton = styled(IconButton)`
  position: absolute !important;
  top: 5px;
  right: 5px;
  background: rgba(255, 255, 255, 0.9) !important;
  width: 30px !important;
  height: 30px !important;

  &:hover {
    background: rgba(255, 255, 255, 1) !important;
  }
`;

export default function ImageAndAssignmentModal({
  page,
  description,
  onChangeDescription,
  style,
  hasDescription = true,
  pageId,
  imageData = [],
  setImageData,
  selectedType,
  maxImages = 10,
  handleAssignment = () => {},
  loadingStates,
  setLoadingStates,
  filteredQuizAndActivityEngagement = [],
  bookId,
  saveHandler,
  showOptions,
  setShowOptions = () => {},
  pageIswhere = { units: false, subUnits: false, subLesson: false },
}) {
  const dispatch = useDispatch();
  const showAlert = useAlert();
  const [dragStates, setDragStates] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const containerRef = useRef(null);
  const [deleteShowAlert, setDeleteShowAlert] = useState(false);

  // Handle single file upload
  const handleSingleFileChange = useCallback(
    async (file, index) => {
      if (!file) return;

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onloadend = function () {
        setImageData((prev) => {
          const updatedData = prev.map((item, itemIndex) =>
            itemIndex === index ? { ...item, resource: reader.result, isUploading: true } : item,
          );
          return updatedData;
        });
      };

      await uploadToStorage(file, pageId, index);
    },
    [setImageData, pageId],
  );

  const uploadToStorage = async (file, pageId, index, uploadKey = `upload_${index}`) => {
    if (!file) return;

    try {
      setLoadingStates((prev) => ({ ...prev, [uploadKey]: true }));

      const onProgress = (percent) => {
        setUploadProgress((prev) => ({ ...prev, [uploadKey]: percent }));
      };

      const downloadURL = await imageUploader(file, onProgress, UPLOAD_IMAGE_TO_FIREBASE);

      if (downloadURL) {
        setImageData((prev) => {
          const updatedData = prev.map((item, itemIndex) =>
            itemIndex === index ? { ...item, resource: downloadURL, isUploading: false } : item,
          );

          saveHandler(updatedData);
          return updatedData;
        });

        showAlert('Image uploaded successfully', 'success');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      Sentry.captureException(`Multi-page upload error: ${error}`);
      console.error('Upload error:', error);
      showAlert('Error uploading image', 'error');

      // Reset the failed item's uploading state
      setImageData((prev) =>
        prev.map((item, itemIndex) =>
          itemIndex === index ? { ...item, isUploading: false, resource: '' } : item,
        ),
      );
    } finally {
      setLoadingStates((prev) => ({ ...prev, [uploadKey]: false }));
      setUploadProgress((prev) => ({ ...prev, [uploadKey]: 0 }));
    }
  };

  const handleAddImages = () => {
    if (imageData.length >= maxImages) {
      showAlert(`Maximum ${maxImages} images allowed`, 'warning');
      return;
    } else if (imageData?.some((item) => item.resource === '')) {
      const emptyIndex = imageData.findIndex((item) => item.resource === '');
      showAlert(`Please upload the empty image slot - ${emptyIndex + 1} first`, 'warning');
      return;
    }

    const imageResource = {
      sequence: imageData.length + 1,
      resource: '',
      isUploading: false,
      id: v4(),
    };
    setImageData((prev) => [...prev, imageResource]);

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleDeleteImage = (targetIndex, sequence) => {
    // Remove item and resequence remaining items
    setImageData((prev) => {
      const filtered = prev.filter((item, index) => index !== targetIndex);
      // Resequence the remaining items
      const data = filtered.map((item, index) => ({
        ...item,
        sequence: index + 1,
      }));
      saveHandler(data);
      return data;
    });

    dispatch(
      handleAdminDeleteEnageActivitySequence({
        book: bookId,
        page: pageId,
        sequence_order: sequence,
      }),
    );
    setDeleteShowAlert(false);
  };

  const onSelectImage = (url, index) => {
    // Update the specific item using array index
    setImageData((prev) => {
      const updatedData = prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, resource: url, isUploading: false } : item,
      );
      saveHandler(updatedData);
      return updatedData;
    });
  };

  const handleDeleteSequence = (element, tindex) => {
    if (element.resource) {
      setDeleteShowAlert({ open: true, sequence: element.id, index: tindex });
      return;
    }
    // Remove item and resequence remaining items
    setImageData((prev) => {
      const filtered = prev.filter((item, index) => index !== tindex);
      // Resequence the remaining items
      return filtered.map((item, index) => ({
        ...item,
        sequence: index + 1,
      }));
    });
  };

  return (
    <SentryWrap>
      <div className="buttonListAddon">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }} className='flex w-full justify-between'>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddImages}
          disabled={imageData.length >= maxImages}
          style={{ marginBottom: 15 }}
        >
          Add Image
        </Button>
        <div>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={showOptions?.length > 0 ? showOptions : "units"}
            onChange={(e) => setShowOptions(e.target.value)}
            aria-labelledby="options-radio-buttons-group"
            name="options-radio-buttons-group"
            style={{ gap: 20 }}
            defaultValue={showOptions?.length > 0 ? showOptions : "units"}
          >
            {pageIswhere?.units && (
              <FormControlLabel
                value="units"
                control={<Radio color="primary" />}
                label={pageIswhere?.unitLabel || "Units"}
              />
            )}

            {pageIswhere?.subUnits && (
              <FormControlLabel
                value="lesson"
                control={<Radio color="primary" />}
                label={pageIswhere?.subUnitLabel || "Lesson"}
              />
            )}

            {pageIswhere?.subLesson && (
              <FormControlLabel
                value="sub-units"
                control={<Radio color="primary" />}
                label={pageIswhere?.subLessonLabel || "Sub-units"}
              />
            )}
          </RadioGroup>
        </FormControl>
        </div>
        </div>

      </div>

      <ImageContainer ref={containerRef} style={style}>
        <DeleteModal
          deleteToggle={deleteShowAlert.open}
          message={`Are you sure you want to delete the delete engage unit and assignment on page?`}
          setDeleteToggle={() => setDeleteShowAlert(false)}
          onDelete={() => handleDeleteImage(deleteShowAlert.index, deleteShowAlert.sequence)}
        />
        {hasDescription && (
          <Page>
            Description:
            <Input
              type="text"
              value={description || ''}
              onChange={(e) => onChangeDescription(e.target.value)}
              placeholder="Enter description..."
            />
          </Page>
        )}

        {/* Image Upload Slots */}
        {imageData?.map((element, index) => {
          const filteredAssignment = filteredQuizAndActivityEngagement.filter(
            (item) => item.config_assignment?.sequence_order === element.id,
          );
          return (
            <div className="flex gap-10px">
              <ImageWrapper
                style={{ width: '50%', height: '419px', overflow: 'auto' }}
                key={`image-${index}-${element.sequence}`}
              >
                <ImageHeader className="buttonListAddon">
                  <Typography variant="subtitle2">
                    {element.isUploading && <span> (Uploading...)</span>}
                  </Typography>

                  <DeleteButton
                    onClick={() => handleDeleteSequence(element, index)}
                    size="small"
                    color="error"
                    style={{ color: 'red' }}
                  >
                    <Delete color='red' fontSize="small" />
                  </DeleteButton>
                </ImageHeader>

                {element.resource && !element.isUploading && (
                  <Container>
                    <Images src={element.resource} alt={`Page ${element.sequence}`} />
                  </Container>
                )}

                {element.isUploading && (
                  <UploadProgress>
                    {/* <LinearProgress
                variant="determinate"
                value={uploadProgress[`upload_${index}`] || 0}
              /> */}
                    <Text>Uploading... {Math.round(uploadProgress[`upload_${index}`] || 0)}%</Text>
                  </UploadProgress>
                )}

                <div className="mt-10px w-full flex justify-center flex-col">
                  <MultiFileUploader inModal onSelect={(url) => onSelectImage(url, index)} />

                  <FileUploader
                    classes="file-uploader-cover answerFileUploader w-full"
                    handleChange={(file) => handleSingleFileChange(file, index)}
                    name="file"
                    types={fileTypes}
                    onDrop={(file) => handleSingleFileChange(file, index)}
                    onDragEnter={() => setDragStates((prev) => ({ ...prev, [index]: true }))}
                    onDragLeave={() => setDragStates((prev) => ({ ...prev, [index]: false }))}
                  >
                    <FileUploadArea isDragOver={dragStates[index]}>
                      {loadingStates[`upload_${index}`] && (
                        <Progress
                          type="line"
                          percent={uploadProgress[`upload_${index}`] || 0}
                          size="small"
                          showInfo={false}
                        />
                      )}

                      <Row
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                      >
                        <Col>
                          <AddToPhotos color="primary" style={{ fontSize: 32 }} />
                        </Col>
                      </Row>

                      <Text>
                        {dragStates[index] ? 'Drop file here' : 'Upload or drag & drop files here'}
                      </Text>

                      <Text style={{ fontSize: 10, marginTop: 4 }}>
                        Supported: {fileTypes.join(', ')}
                      </Text>
                    </FileUploadArea>
                  </FileUploader>
                </div>
              </ImageWrapper>

              <ImageWrapper style={{ width: '50%', height: '419px', overflow: 'auto' }}>
                <>
                  <div className="buttonListAddon">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (!element.resource) {
                          showAlert('Please upload an image first', 'error');
                          return;
                        }
                        handleAssignment(element, index);
                      }}
                      startIcon={<Add />}
                    >
                      Assignment
                    </Button>
                  </div>

                  {filteredAssignment.length > 0 && (
                    <div className="mt-10px">
                      <AdminActivity
                        pageActivities={filteredAssignment}
                        page={page}
                        pageId={pageId}
                        bookId={bookId}
                      />
                    </div>
                  )}
                </>
              </ImageWrapper>
            </div>
          );
        })}

        {imageData.length === 0 && selectedType === 'uploader' && (
          <Container empty>
            <Text style={{ fontSize: 14, color: '#999' }}>
              No images added yet. Use the buttons above to start uploading.
            </Text>
          </Container>
        )}
      </ImageContainer>
    </SentryWrap>
  );
}
